import { Button, ErrorText, Field, Spinner } from '~/ui/primitives';
import { formatDuration } from '~/ui/format';
import { StepHeading } from './StepHeading';

/**
 * The one sentence that may name how long the code lasts.
 *
 * The OTP TTL is a wallet-api DEPLOYMENT config value, and `email/start`
 * reports the live one as `expiresInSeconds`. So the duration is rendered
 * from what arrived and from nothing else: a hardcoded "10 minutes" becomes a
 * user-visible lie the moment an operator changes the knob, and it would lie
 * silently — nothing in this app can tell. When no value arrived (an older
 * server, or a caller that has not captured one), fall back to saying only
 * what is certainly true.
 */
function expiryPhrase(expiresInSeconds: number | null | undefined): string {
  if (typeof expiresInSeconds !== 'number' || !Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
    return 'It expires shortly.';
  }
  return `It expires in ${formatDuration(Math.round(expiresInSeconds))}.`;
}

/**
 * Step 2 of 3. Exchanges the emailed code for a single-use `emailToken`. Only
 * this response — to a caller who has just proven mailbox control — may say
 * the address is already registered, which is why the sign-in branch lives
 * here and not on step 1.
 *
 * Nothing has been created yet at this point: abandoning here leaves an
 * expired verification row and no account, no credential.
 */
export function OtpStep({
  email,
  headingLevel = 1,
  headingId,
  autoFocus = false,
  expiresInSeconds,
  code,
  onCodeChange,
  onVerify,
  onResend,
  onBack,
  onCancel,
  cancelLabel = 'Cancel',
  busy,
  resending,
  resent,
  error,
}: {
  email: string;
  /** 1 (default) for a page-level step; 2 when rendered inside a card. See `EmailStep`. */
  headingLevel?: 1 | 2;
  /** DOM id for the heading, so a `role="dialog"` caller can `aria-labelledby` it. */
  headingId?: string;
  /**
   * Focus the code input on mount. See `EmailStep.autoFocus`.
   *
   * GUARDED ELSEWHERE: same as `EmailStep.autoFocus` — the only test that
   * depends on this is in `src/popup/components/__tests__/RegisterPrompt.test.tsx`,
   * not in a test file for this component (there isn't one).
   */
  autoFocus?: boolean;
  /** The live OTP lifetime from `accounts.emailStart`; see `expiryPhrase`. */
  expiresInSeconds?: number | null;
  code: string;
  onCodeChange: (v: string) => void;
  onVerify: () => void;
  onResend: () => void;
  onBack: () => void;
  /**
   * Abandon the whole flow, not just this step — mirrors `EmailStep.onCancel`,
   * and renders nothing when absent so the two page-level callers (`/register`
   * and the Settings nudge) are untouched.
   *
   * The embedded register popup supplies it because there is a caller waiting
   * on the other side of a `postMessage`: a reader who gives up at the code
   * step has to be able to say so, or the host page is left holding a promise
   * that only a timeout will settle. `onBack` is not that — it goes back one
   * step and stays in the flow.
   */
  onCancel?: () => void;
  cancelLabel?: string;
  busy: boolean;
  resending: boolean;
  resent: boolean;
  error: string | null;
}) {
  const canSubmit = code.trim().length > 0 && !busy;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
        <StepHeading level={headingLevel} id={headingId}>
          Check your inbox
        </StepHeading>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          We sent a 6-digit code to <strong>{email}</strong>. {expiryPhrase(expiresInSeconds)}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Field
          label="Code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={autoFocus}
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onVerify();
          }}
          placeholder="428913"
        />
        <Button onClick={onVerify} disabled={!canSubmit}>
          {busy ? (
            <>
              <Spinner /> Verifying…
            </>
          ) : (
            'Verify'
          )}
        </Button>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <Button variant="tertiary" size="sm" onClick={onResend} disabled={busy || resending}>
            {resending ? 'Sending…' : resent ? 'Code sent again' : 'Send a new code'}
          </Button>
          <Button variant="tertiary" size="sm" onClick={onBack} disabled={busy}>
            Use a different address
          </Button>
        </div>
        {onCancel && (
          <div>
            <Button variant="tertiary" size="sm" fullWidth={false} onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          </div>
        )}
      </div>
      {error && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <ErrorText>{error}</ErrorText>
        </div>
      )}
    </>
  );
}
